import type {
  ArchiveEmployeeRequest,
  PositionView,
  UpdateEmployeeProfileRequest,
  CreateEmployeeTimeOffRequest,
  ReassignEmployeeDayRequest,
  ReassignEmployeeDayResult,
  StaffVisibilityView,
  UpsertServiceRequest,
  UpsertWorkdayRequest,
} from "@planerius/contracts/cabinet"

import { apiRequest } from "./http"
import { newIdempotencyKey } from "./idempotency"

/**
 * Справочники салона: каталог услуг (3-Б1, 3-Б2), сотрудники (3-Б5), график (3-Б8),
 * настройки филиала — и то, что из них собирается чек-лист первого запуска (4-А2).
 *
 * ПОЧЕМУ ЗДЕСЬ ЕСТЬ РУЧНОЙ РАЗБОР ОТВЕТА, КОТОРОГО НЕТ У ЖУРНАЛА И КЛИЕНТОВ.
 *
 * Журнал и клиенты отдают формы из пакета контрактов: ScheduleResult, ClientListResult.
 * Справочники — нет. Контроллер /v1/cabinet/catalog объявлен как `Promise<unknown[]>`
 * и отдаёт СТРОКИ БАЗЫ как есть: `duration_minutes`, `is_online_bookable`, `branch_id`.
 * Схемы ответа в контрактах нет вовсе — там описаны только запросы (UpsertServiceRequest
 * и соседние). Это разрыв в API, он назван в сводке этапа.
 *
 * Пока разрыв не закрыт, граница проходит здесь, в одном файле: наружу этот модуль отдаёт
 * нормальные camelCase-виды, а знание о снейк-кейсе не расползается по экранам. Когда
 * сервер начнёт отдавать форму из контрактов, меняется этот файл — и ни один экран.
 *
 * Разбор ЩАДЯЩИЙ: незнакомое поле не роняет экран, отсутствующее — превращается
 * в осмысленное умолчание. Справочник на 200 услуг, который не показался целиком
 * из-за одной кривой строки, хуже, чем справочник с одной странной строкой.
 */

// ───────────────────────── щадящий разбор строки ─────────────────────────

type Row = Readonly<Record<string, unknown>>

function rows(value: unknown): readonly Row[] {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is Row => typeof item === "object" && item !== null)
}

function str(row: Row, key: string): string | null {
  const value = row[key]
  return typeof value === "string" && value.length > 0 ? value : null
}

function num(row: Row, key: string, fallback: number): number {
  const value = row[key]
  if (typeof value === "number" && Number.isFinite(value)) return value
  // Числа из PostgreSQL приезжают строкой, когда тип bigint или numeric: «1800» вместо 1800.
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return fallback
}

function numOrNull(row: Row, key: string): number | null {
  const value = row[key]
  if (value === null || value === undefined) return null
  return num(row, key, 0)
}

function bool(row: Row, key: string, fallback: boolean): boolean {
  const value = row[key]
  return typeof value === "boolean" ? value : fallback
}

function idList(row: Row, key: string): readonly string[] {
  const value = row[key]
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === "string")
}

// ───────────────────────── каталог ─────────────────────────

export interface ServiceCategoryView {
  readonly id: string
  readonly parentId: string | null
  readonly name: string
  readonly ord: number
  readonly isActive: boolean
}

/** Шаг составной услуги. Шаг, который не занимает мастера, — это и есть «пауза». */
export interface ServiceStepView {
  readonly id: string | null
  readonly ord: number
  readonly name: string | null
  readonly durationMinutes: number
  readonly occupiesEmployee: boolean
  readonly occupiesResource: boolean
}

export interface ServiceView {
  readonly id: string
  readonly branchId: string | null
  readonly categoryId: string | null
  readonly kind: string
  readonly name: string
  readonly description: string | null
  /** Работа мастера, которую видит клиент. Буферы сюда НЕ входят — они рядом. */
  readonly durationMinutes: number
  readonly bufferBeforeMinutes: number
  readonly bufferAfterMinutes: number
  readonly capacity: number
  readonly priceMinor: number
  readonly isOnlineBookable: boolean
  readonly isActive: boolean
  readonly ord: number
  readonly steps: readonly ServiceStepView[]
  /** Сколько типов ресурсов и сколько штук требует услуга: «Кресло — 1». */
  readonly resourceRequirements: readonly ServiceResourceView[]
  /** Будущие записи на услугу — их раскладка правкой шагов не меняется. */
  readonly futureAppointments: number
  /** Версия для сохранения: двое правят прайс — второй не затирает первого молча. */
  readonly updatedAt: string | null
}

export interface ServiceResourceView {
  readonly resourceTypeId: string
  readonly name: string
  readonly qty: number
}

export interface ResourceTypeView {
  readonly id: string
  readonly name: string
  readonly isActive: boolean
}

export interface CatalogView {
  readonly categories: readonly ServiceCategoryView[]
  readonly services: readonly ServiceView[]
  readonly resourceTypes: readonly ResourceTypeView[]
}

function toStep(row: Row): ServiceStepView {
  return {
    id: str(row, "id"),
    ord: num(row, "ord", 0),
    name: str(row, "name"),
    durationMinutes: num(row, "durationMinutes", 0),
    occupiesEmployee: bool(row, "occupiesEmployee", true),
    occupiesResource: bool(row, "occupiesResource", false),
  }
}

function toService(row: Row): ServiceView {
  return {
    id: str(row, "id") ?? "",
    branchId: str(row, "branch_id"),
    categoryId: str(row, "category_id"),
    kind: str(row, "kind") ?? "simple",
    name: str(row, "name") ?? "Без названия",
    description: str(row, "description"),
    durationMinutes: num(row, "duration_minutes", 0),
    bufferBeforeMinutes: num(row, "buffer_before_minutes", 0),
    bufferAfterMinutes: num(row, "buffer_after_minutes", 0),
    capacity: num(row, "capacity", 1),
    priceMinor: num(row, "price_minor", 0),
    isOnlineBookable: bool(row, "is_online_bookable", false),
    isActive: bool(row, "is_active", true),
    ord: num(row, "ord", 0),
    // Шаги собирает json_agg на сервере, поэтому внутри уже camelCase — в отличие
    // от самой строки услуги. Разнобой не мой, но жить с ним приходится здесь.
    steps: rows(row["steps"]).map(toStep),
    resourceRequirements: rows(row["resource_requirements"]).map((item) => ({
      resourceTypeId: str(item, "resourceTypeId") ?? "",
      name: str(item, "name") ?? "Ресурс",
      qty: num(item, "qty", 1),
    })),
    futureAppointments: num(row, "future_appointments", 0),
    updatedAt: str(row, "updated_at"),
  }
}

export function normalizeCatalog(body: unknown): CatalogView {
  const root = typeof body === "object" && body !== null ? (body as Row) : {}
  return {
    categories: rows(root["categories"]).map((row) => ({
      id: str(row, "id") ?? "",
      parentId: str(row, "parent_id"),
      name: str(row, "name") ?? "Без названия",
      ord: num(row, "ord", 0),
      isActive: bool(row, "is_active", true),
    })),
    services: rows(root["services"]).map(toService),
    resourceTypes: rows(root["resourceTypes"]).map((row) => ({
      id: str(row, "id") ?? "",
      name: str(row, "name") ?? "Ресурс",
      isActive: bool(row, "is_active", true),
    })),
  }
}

export async function fetchCatalog(signal?: AbortSignal): Promise<CatalogView> {
  return normalizeCatalog(await apiRequest<unknown>("/cabinet/catalog", { signal }))
}

export function upsertService(request: UpsertServiceRequest): Promise<{ readonly id: string }> {
  return apiRequest<{ id: string }>("/cabinet/catalog/services", {
    method: "POST",
    body: request,
    idempotencyKey: newIdempotencyKey("service-save"),
  })
}

// ───────────────────────── сотрудники ─────────────────────────

export interface EmployeeView {
  readonly id: string
  readonly displayName: string
  readonly publicName: string | null
  readonly positionId: string | null
  readonly positionName: string | null
  readonly branchDefaultId: string | null
  readonly color: string | null
  readonly avatarUrl: string | null
  /** Описание для витрины (3-Б7). */
  readonly about: string | null
  /**
   * Исполнитель ли. От этого зависит и тариф («в тарифе» / «бесплатно»), и сама
   * возможность записать к нему клиента: администратор и владелец — не исполнители.
   */
  readonly isBookable: boolean
  /** Виден ли на витрине онлайн-записи. У неисполнителя смысла не имеет. */
  readonly isOnlineBookable: boolean
  readonly isBillable: boolean
  readonly archivedAt: string | null
  readonly updatedAt: string | null
  /** Услуги, которые мастер делает. Пустой список — «услуги не отмечены» в 3-Б5. */
  readonly serviceIds: readonly string[]
}

export function normalizeEmployees(body: unknown): readonly EmployeeView[] {
  return rows(body).map((row) => ({
    id: str(row, "id") ?? "",
    displayName: str(row, "display_name") ?? "Без имени",
    publicName: str(row, "public_name"),
    positionId: str(row, "position_id"),
    positionName: str(row, "position_name"),
    branchDefaultId: str(row, "branch_default_id"),
    color: str(row, "color"),
    avatarUrl: str(row, "avatar_url"),
    about: str(row, "about"),
    isBookable: bool(row, "is_bookable", false),
    isOnlineBookable: bool(row, "is_online_bookable", false),
    isBillable: bool(row, "is_billable", false),
    archivedAt: str(row, "archived_at"),
    updatedAt: str(row, "updated_at"),
    serviceIds: idList(row, "service_ids"),
  }))
}

export async function fetchEmployees(
  includeArchived = false,
  signal?: AbortSignal,
): Promise<readonly EmployeeView[]> {
  const body = await apiRequest<unknown>("/cabinet/catalog/employees", {
    query: { includeArchived },
    signal,
  })
  return normalizeEmployees(body)
}

/**
 * Архивация мастера — «уволилась навсегда» из 2-2.10.4.
 *
 * expectedUpdatedAt обязателен: пока окно было открыто, мастера могли перевести
 * в другой филиал или заархивировать вторым администратором, и применять решение
 * к версии, которой человек не видел, нельзя.
 */
export function archiveEmployee(request: ArchiveEmployeeRequest): Promise<void> {
  return apiRequest<void>("/cabinet/catalog/employees/archive", {
    method: "POST",
    body: request,
    idempotencyKey: newIdempotencyKey("employee-archive"),
  })
}

/** Должности салона — выбор в профиле мастера (3-Б7). */
export async function fetchPositions(signal?: AbortSignal): Promise<readonly PositionView[]> {
  const body = await apiRequest<{ readonly items: readonly PositionView[] }>("/cabinet/catalog/positions", { signal })
  return body.items
}

/**
 * Публичный профиль мастера (3-Б7): только поля витрины, с версией карточки — правка
 * описания не затирает тариф и услуги, которые в ту же минуту меняет администратор.
 */
export function updateEmployeeProfile(
  request: UpdateEmployeeProfileRequest,
): Promise<{ readonly id: string; readonly updatedAt: string }> {
  return apiRequest("/cabinet/catalog/employees/profile", { method: "POST", body: request })
}

// ───────────────────────── график смен ─────────────────────────

/**
 * Правка одного дня поверх шаблона (3-Б8, вид «правка поверх шаблона»).
 * Ложится отдельной строкой источника override и шаблон не трогает.
 */
export function upsertWorkday(request: UpsertWorkdayRequest): Promise<unknown> {
  return apiRequest<unknown>("/cabinet/staff-schedule/workdays", {
    method: "POST",
    body: request,
    idempotencyKey: newIdempotencyKey("workday-save"),
  })
}

export interface TimeOffResult {
  readonly inserted: number
  readonly unassignedAppointmentIds: readonly string[]
}

/** Отсутствие мастера на период: «смены Анны до пятницы закроются отсутствием». */
export function createTimeOff(request: CreateEmployeeTimeOffRequest): Promise<TimeOffResult> {
  return apiRequest<TimeOffResult>("/cabinet/time-off", {
    method: "POST",
    body: request,
    idempotencyKey: newIdempotencyKey("time-off"),
  })
}

/** Передача дня другому мастеру. Одна транзакция; частичный успех — норма, не сбой. */
export function reassignDay(
  request: ReassignEmployeeDayRequest,
): Promise<ReassignEmployeeDayResult> {
  return apiRequest<ReassignEmployeeDayResult>("/cabinet/reassign-day", {
    method: "POST",
    body: request,
    idempotencyKey: newIdempotencyKey("reassign-day"),
  })
}

// ───────────────────────── настройки филиала ─────────────────────────

/*
 * Филиал в настройках описан КОНТРАКТОМ (packages/contracts → BranchSettingsView):
 * своего описания здесь больше нет. Два описания одной строки разъезжаются на первой
 * же правке сервера, и разъезд этот молчаливый — экран показывает умолчания вместо
 * настоящих значений. Имена оставлены прежними, чтобы экраны не переписывать.
 */
import type { BranchHoursRow, BranchSettingsView } from "@planerius/contracts/cabinet"

export type { BranchHoursRow, BranchSettingsView }

/**
 * Разбор ответа филиалов.
 *
 * Ответ сервера ТИПИЗИРОВАН (BranchSettingsView в контракте), и разбирать его наугад
 * больше не надо: раньше здесь читались snake_case-поля, и любое переименование на
 * сервере молча превращалось в «шаг 15 минут» и «телефона нет». Функция оставлена
 * ради проверок, которые кормят экран готовым объектом.
 */
export function normalizeBranches(body: unknown): readonly BranchSettingsView[] {
  return Array.isArray(body) ? (body as readonly BranchSettingsView[]) : []
}

export async function fetchBranchSettings(
  signal?: AbortSignal,
): Promise<readonly BranchSettingsView[]> {
  return normalizeBranches(
    await apiRequest<unknown>("/cabinet/settings/branches", { signal }),
  )
}

/**
 * Что мастеру видно (4-Б12). Экранам справочников это нужно ровно для одного:
 * объяснить, ПОЧЕМУ в карточке клиента вместо номера маска, — «номер закрыт настройкой
 * салона», а не «номер не загрузился».
 */
export function fetchStaffVisibility(signal?: AbortSignal): Promise<StaffVisibilityView> {
  return apiRequest<StaffVisibilityView>("/cabinet/settings/staff-visibility", { signal })
}

/** Правила записи филиала. Из них берутся сроки в текстах последствий. */
export interface BookingRulesRow {
  readonly branchId: string | null
  readonly minLeadMinutes: number
  readonly maxHorizonDays: number
  readonly cancelDeadlineMinutes: number
  readonly quarantineMinutes: number
  readonly noShowThreshold: number | null
}

export function normalizeBookingRules(body: unknown): readonly BookingRulesRow[] {
  return rows(body).map((row) => ({
    branchId: str(row, "branch_id"),
    minLeadMinutes: num(row, "min_lead_minutes", 0),
    maxHorizonDays: num(row, "max_horizon_days", 90),
    cancelDeadlineMinutes: num(row, "cancel_deadline_minutes", 0),
    quarantineMinutes: num(row, "quarantine_minutes", 0),
    noShowThreshold: numOrNull(row, "no_show_threshold"),
  }))
}

export async function fetchBookingRules(signal?: AbortSignal): Promise<readonly BookingRulesRow[]> {
  return normalizeBookingRules(
    await apiRequest<unknown>("/cabinet/settings/booking-rules", { signal }),
  )
}
